<template>
  <div class="withdraw">
    <button class="btn-back" @click="router.push({ name: 'player.wallet' })">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="m15 18-6-6 6-6"/>
      </svg>
      {{ $t('player.withdraw.back') }}
    </button>

    <h2 class="page-title">{{ $t('player.withdraw.title') }}</h2>

    <!-- Balance Summary -->
    <div v-if="walletStore.balance" class="balance-summary">
      <span class="balance-label">{{ $t('player.withdraw.available_balance') }}:</span>
      <span class="balance-value">{{ formatAmount(walletStore.balance.balance) }}</span>
      <span class="balance-currency">{{ walletStore.balance.currency }}</span>
    </div>

    <!-- KYC Warning Banner -->
    <div v-if="userStore.user && userStore.user.kyc_status !== 'approved'" class="warning-banner warning-kyc">
      <div class="warning-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <div class="warning-text">
        <p class="warning-title">{{ $t('player.withdraw.kyc_title') }}</p>
        <p class="warning-detail">{{ $t('player.withdraw.kyc_desc') }}</p>
      </div>
    </div>

    <!-- Active Bonus Warning -->
    <div v-if="walletStore.balance && walletStore.balance.bonus_balance > 0" class="warning-banner warning-bonus">
      <div class="warning-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      </div>
      <div class="warning-text">
        <p class="warning-title">{{ $t('player.withdraw.bonus_title') }}</p>
        <p class="warning-detail">{{ $t('player.withdraw.bonus_desc') }}</p>
      </div>
    </div>

    <!-- Withdraw Form Card -->
    <div class="withdraw-card">
      <!-- Payment Method Tabs -->
      <div class="form-group">
        <label class="form-label">{{ $t('player.withdraw.payment_method') }}</label>
        <div class="method-tabs">
          <button
            v-for="method in paymentMethods"
            :key="method.value"
            class="method-tab"
            :class="{ active: selectedMethod === method.value }"
            @click="selectedMethod = method.value"
          >
            <span class="method-icon" v-html="sanitizeSvg(method.icon)"></span>
            <span class="method-name">{{ method.label }}</span>
          </button>
        </div>
      </div>

      <!-- Amount -->
      <div class="form-group">
        <label class="form-label" for="amount">{{ $t('player.withdraw.amount_label') }}</label>
        <div class="amount-input-wrap">
          <span class="amount-prefix">$</span>
          <input
            id="amount"
            v-model="amount"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            class="form-input amount-input"
          >
        </div>
        <div class="quick-amounts">
          <button
            v-for="qa in quickAmounts"
            :key="qa"
            class="quick-amount-btn"
            @click="amount = String(qa)"
          >
            ${{ qa }}
          </button>
          <button
            v-if="walletStore.balance"
            class="quick-amount-btn quick-amount-max"
            @click="amount = String(walletStore.balance.balance)"
          >
            {{ $t('common.max') }}
          </button>
        </div>
      </div>

      <!-- Destination Address -->
      <div class="form-group">
        <label class="form-label" for="address">
          {{ selectedMethod === 'crypto' ? $t('player.withdraw.address_crypto') : $t('player.withdraw.address_bank') }}
        </label>
        <input
          id="address"
          v-model="address"
          type="text"
          :placeholder="selectedMethod === 'crypto' ? $t('player.withdraw.placeholder_crypto') : $t('player.withdraw.placeholder_bank')"
          class="form-input"
        >
        <p class="form-hint">
          {{ selectedMethod === 'crypto'
            ? $t('player.withdraw.hint_crypto')
            : $t('player.withdraw.hint_bank') }}
        </p>
      </div>

      <!-- Withdrawal Limits Info -->
      <div class="limits-info">
        <div class="limits-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>
        <div class="limits-text">
          <p class="limits-title">{{ $t('player.withdraw.limits_title') }}</p>
          <p class="limits-detail">{{ $t('player.withdraw.limits_detail') }}</p>
        </div>
      </div>

      <!-- Jurisdiction: payment method restriction notice -->
      <JurisdictionBlockNotice
        v-if="paymentMethodRestricted"
        type="payment"
        :message="paymentRestrictionMessage"
        class="jurisdiction-notice"
      />

      <!-- Error Messages -->
      <div v-if="walletStore.withdrawalError || formError" class="error-banner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span>{{ walletStore.withdrawalError || formError }}</span>
      </div>

      <!-- Success Message -->
      <div v-if="withdrawalSuccess" class="success-banner">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <span>{{ withdrawalSuccess }}</span>
      </div>

      <!-- Submit Button -->
      <button
        class="btn-withdraw"
        :disabled="!canSubmit"
        @click="submitWithdrawal"
      >
        <span v-if="walletStore.loadingWithdrawal" class="spinner"></span>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/>
        </svg>
        {{ walletStore.loadingWithdrawal ? $t('common.processing') : $t('wallet.withdraw') }}
      </button>
    </div>

    <!-- Pending Withdrawals -->
    <div class="history-section">
      <h3 class="section-subtitle">{{ $t('player.withdraw.pending') }}</h3>
      <div v-if="walletStore.loadingPendingWithdrawals" class="history-loading">
        <span class="spinner spinner-sm"></span>
        {{ $t('player.withdraw.loading') }}
      </div>
      <div v-else-if="walletStore.pendingWithdrawals.length > 0" class="history-list">
        <div v-for="tx in walletStore.pendingWithdrawals" :key="tx.id" class="history-item">
          <div class="history-item-left">
            <span class="history-type">{{ $t('player.withdraw.type_label') }}</span>
            <span class="history-date">{{ formatDate(tx.created_at) }}</span>
          </div>
          <div class="history-item-right">
            <span class="history-amount">-{{ formatAmount(Math.abs(tx.amount)) }}</span>
            <span class="status-badge pending">
              <span class="polling-dot"></span>
              {{ $t('player.deposit.status_pending') }}
            </span>
            <button
              class="btn-cancel"
              :disabled="cancellingId === tx.id"
              @click="handleCancel(tx.id)"
            >
              {{ cancellingId === tx.id ? $t('player.withdraw.cancelling') : $t('common.cancel') }}
            </button>
          </div>
        </div>
      </div>
      <div v-else class="history-empty">
        <p>{{ $t('player.withdraw.no_pending') }}</p>
        <p class="history-empty-sub">{{ $t('player.withdraw.no_pending_sub') }}</p>
      </div>
    </div>

    <!-- Recent Withdrawal History -->
    <div class="history-section">
      <h3 class="section-subtitle">{{ $t('player.withdraw.recent') }}</h3>
      <div v-if="recentWithdrawals.length > 0" class="history-list">
        <div v-for="tx in recentWithdrawals" :key="tx.id" class="history-item">
          <div class="history-item-left">
            <span class="history-type">{{ $t('player.withdraw.type_label') }}</span>
            <span class="history-date">{{ formatDate(tx.created_at) }}</span>
          </div>
          <div class="history-item-right">
            <span class="history-amount">-{{ formatAmount(Math.abs(tx.amount)) }}</span>
            <span class="status-badge" :class="tx.status">{{ tx.status }}</span>
          </div>
        </div>
      </div>
      <div v-else class="history-empty">
        <p>{{ $t('player.withdraw.no_history') }}</p>
        <p class="history-empty-sub">{{ $t('player.withdraw.no_history_sub') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useWalletStore } from '@/stores/wallet'
import { useUserStore } from '@/stores/user'
import { useToastStore } from '@/stores/toast'
import JurisdictionBlockNotice from '@/components/JurisdictionBlockNotice.vue'
import { useJurisdiction } from '@/composables/useJurisdiction'
import { sanitizeSvg } from '@/composables/useSanitize'

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const userStore = useUserStore()
const toast = useToastStore()
const { jurisdiction, fetchJurisdiction } = useJurisdiction()

// Show a notice when jurisdiction restricts available payment methods
const paymentMethodRestricted = computed(() => {
  return jurisdiction.value?.allowed_payment_methods !== null &&
    (jurisdiction.value?.allowed_payment_methods?.length ?? 0) > 0
})

const paymentRestrictionMessage = computed(() => {
  const allowed = jurisdiction.value?.allowed_payment_methods
  if (!allowed || allowed.length === 0) return undefined
  const list = allowed.join(', ')
  return t('player.withdraw.payment_restriction', { list })
})

const amount = ref('')
const address = ref('')
const selectedMethod = ref('manual')
const formError = ref<string | null>(null)
const withdrawalSuccess = ref<string | null>(null)
const cancellingId = ref<number | null>(null)

const quickAmounts = [25, 50, 100, 250, 500, 1000]

const paymentMethods = computed(() => [
  {
    value: 'manual',
    label: t('player.withdraw.method_bank_transfer'),
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>',
  },
  {
    value: 'crypto',
    label: t('player.withdraw.method_cryptocurrency'),
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
  },
])

const canSubmit = computed(() => {
  const numAmount = parseFloat(amount.value)
  return numAmount > 0 && address.value.trim().length > 0 && !walletStore.loadingWithdrawal
})

const recentWithdrawals = computed(() => {
  return walletStore.transactions
    .filter((tx) => tx.type === 'debit' && (tx.notes?.toLowerCase().includes('withdrawal') ?? false))
    .slice(0, 5)
})

function formatAmount(val: number): string {
  return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

async function submitWithdrawal() {
  formError.value = null
  withdrawalSuccess.value = null
  walletStore.withdrawalError = null

  const numAmount = parseFloat(amount.value)
  if (!numAmount || numAmount <= 0) {
    formError.value = t('player.withdraw.invalid_amount')
    return
  }

  if (!address.value.trim()) {
    formError.value = t('player.withdraw.no_address')
    return
  }

  const currency = walletStore.balance?.currency || 'USD'

  try {
    const response = await walletStore.submitWithdrawal(
      numAmount,
      currency,
      selectedMethod.value,
      address.value.trim(),
    )

    if (response.status === 'completed') {
      withdrawalSuccess.value = response.message || t('player.withdraw.success_completed')
      toast.success(t('player.withdraw.toast_completed'))
    } else {
      withdrawalSuccess.value = response.message || t('player.withdraw.success_pending')
      toast.success(t('player.withdraw.toast_pending'))
    }

    // Reset form
    amount.value = ''
    address.value = ''

    // Refresh transaction history
    walletStore.fetchTransactions(1)
  } catch {
    // Error is already set in walletStore.withdrawalError
  }
}

async function handleCancel(transactionId: number) {
  cancellingId.value = transactionId
  walletStore.withdrawalError = null

  try {
    await walletStore.cancelWithdrawal(transactionId)
    toast.success(t('player.withdraw.cancelled'))
    walletStore.fetchTransactions(1)
  } catch {
    toast.error(walletStore.withdrawalError || t('player.withdraw.cancel_failed'))
  } finally {
    cancellingId.value = null
  }
}

onMounted(() => {
  if (!walletStore.balance) {
    walletStore.fetchBalance()
  }
  walletStore.fetchPendingWithdrawals()
  walletStore.fetchTransactions(1)
  fetchJurisdiction()
})
</script>

<style scoped>
.withdraw {
  padding: var(--space-6, 24px);
  max-width: 600px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Back Button */
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: color var(--transition-fast, 150ms ease);
  margin-bottom: 16px;
}

.btn-back:hover {
  color: var(--text-primary, #fff);
}

/* Page Title */
.page-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
}

/* Balance Summary */
.balance-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(0, 231, 1, 0.06);
  border: 1px solid rgba(0, 231, 1, 0.15);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
}

.balance-label {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.balance-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 15px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

.balance-currency {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
}

/* Warning Banners */
.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--radius-md, 8px);
  margin-bottom: 12px;
}

.warning-kyc {
  background: rgba(255, 167, 38, 0.08);
  border: 1px solid rgba(255, 167, 38, 0.2);
}

.warning-bonus {
  background: rgba(20, 117, 225, 0.08);
  border: 1px solid rgba(20, 117, 225, 0.2);
}

.warning-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.warning-kyc .warning-icon {
  color: var(--color-warning, #ffa726);
}

.warning-bonus .warning-icon {
  color: var(--accent-secondary, #1475e1);
}

.warning-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 2px;
}

.warning-detail {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.4;
}

/* Withdraw Card */
.withdraw-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  margin-bottom: 24px;
}

/* Form */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 11px 14px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  font-size: 14px;
  font-family: inherit;
  transition: border-color var(--transition-fast, 150ms ease);
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-hint {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 6px;
}

/* Payment Method Tabs */
.method-tabs {
  display: flex;
  gap: 10px;
}

.method-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: var(--bg-primary, #0f1923);
  border: 2px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--transition-fast, 150ms ease);
}

.method-tab:hover {
  border-color: rgba(255, 255, 255, 0.15);
  color: var(--text-primary, #fff);
}

.method-tab.active {
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.06);
}

.method-icon {
  display: flex;
  align-items: center;
}

/* Amount Input */
.amount-input-wrap {
  position: relative;
}

.amount-prefix {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
}

.amount-input {
  padding-left: 32px;
}

.amount-input::-webkit-inner-spin-button,
.amount-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.amount-input[type="number"] {
  -moz-appearance: textfield;
}

/* Quick Amounts */
.quick-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.quick-amount-btn {
  padding: 6px 14px;
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-sm, 4px);
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--transition-fast, 150ms ease);
}

.quick-amount-btn:hover {
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.06);
}

.quick-amount-max {
  border-color: rgba(20, 117, 225, 0.3);
  color: var(--accent-secondary, #1475e1);
}

.quick-amount-max:hover {
  border-color: var(--accent-secondary, #1475e1);
  color: var(--accent-secondary, #1475e1);
  background: rgba(20, 117, 225, 0.08);
}

/* Withdrawal Limits Info */
.limits-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(255, 193, 7, 0.06);
  border: 1px solid rgba(255, 193, 7, 0.15);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
}

.limits-icon {
  color: var(--color-warning, #ffc107);
  flex-shrink: 0;
  margin-top: 1px;
}

.limits-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 2px;
}

.limits-detail {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.4;
}

/* Jurisdiction notice */
.jurisdiction-notice {
  margin-bottom: 20px;
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
  color: var(--color-danger, #ed4245);
  font-size: 13px;
  font-weight: 500;
}

/* Success Banner */
.success-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(0, 231, 1, 0.08);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 20px;
  color: var(--color-success, #00e701);
  font-size: 13px;
  font-weight: 500;
}

/* Withdraw Button */
.btn-withdraw {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 24px;
  font-size: 15px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  font-family: inherit;
  background: var(--color-danger, #ed4245);
  color: #fff;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-withdraw:hover:not(:disabled) {
  background: #d93a3d;
  box-shadow: 0 0 24px rgba(237, 66, 69, 0.35);
  transform: translateY(-1px);
}

.btn-withdraw:disabled {
  background: var(--bg-tertiary, #213743);
  color: var(--text-disabled, #4a5568);
  cursor: not-allowed;
  opacity: 0.6;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border-width: 1.5px;
  border-color: rgba(177, 186, 211, 0.3);
  border-top-color: var(--text-secondary, #b1bad3);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* History Section */
.history-section {
  margin-bottom: 24px;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
}

.history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 20px;
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
}

.history-list {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background var(--transition-fast, 150ms ease);
}

.history-item:last-child {
  border-bottom: none;
}

.history-item:hover {
  background: rgba(0, 0, 0, 0.1);
}

.history-item-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.history-type {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.history-date {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.history-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.history-amount {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--color-danger, #ed4245);
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: var(--radius-md, 8px);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.status-badge.completed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.status-badge.failed,
.status-badge.cancelled {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Polling Dot Animation */
.polling-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-warning, #ffa726);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* Cancel Button */
.btn-cancel {
  padding: 5px 12px;
  background: transparent;
  border: 1px solid rgba(237, 66, 69, 0.3);
  border-radius: var(--radius-sm, 4px);
  color: var(--color-danger, #ed4245);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-cancel:hover:not(:disabled) {
  background: rgba(237, 66, 69, 0.1);
  border-color: var(--color-danger, #ed4245);
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  gap: 6px;
}

.history-empty-sub {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Responsive */
@media (max-width: 768px) {
  .withdraw {
    padding: var(--space-4, 16px);
  }

  .method-tabs {
    flex-direction: column;
  }

  .quick-amounts {
    justify-content: center;
  }

  .history-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .history-item-right {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
